import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Toaster, toast } from 'sonner';
import "./App.css";
import ContentManager from "./ContentManager";
import { db } from "./firebase";
import { ref, query, orderByChild, equalTo, onValue } from "firebase/database";
import { ContractPage, OnboardingPage } from "./OnboardingPages";
import { 
  trackBookingInquiry, trackBookingSubmitted, trackAddonsSelected, 
  trackPaymentInitiated, trackPaymentCompleted, trackPaymentFailed,
  trackWhatsAppClick, trackPhoneCall, trackPackageViewed, trackSectionViewed
} from "./analyticsTracker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Assets - Local independent paths
const ASSETS = {
  logo: "/logo.png",
  heroBg: "/hero-bg.png",
};

// Auth Context
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      axios.get(`${API}/auth/me`).then(res => setUser(res.data.user)).catch(() => logout());
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
  };

  return <AuthContext.Provider value={{ user, token, login, logout }}>{children}</AuthContext.Provider>;
};

const useAuth = () => useContext(AuthContext);

// Icons
const Icons = {
  Play: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>,
  Check: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  Phone: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  Calendar: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  Clock: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  Star: ({ filled }) => <svg width="20" height="20" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  ArrowRight: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
  Shield: () => <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  Zap: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  Camera: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  Users: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Menu: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
  X: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  ChevronDown: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>,
  ChevronUp: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="18 15 12 9 6 15"/></svg>,
  Music: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>,
  MessageCircle: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
  WhatsApp: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
  Instagram: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>,
  Facebook: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
  LinkedIn: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
  Dashboard: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  Package: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
  Plus: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  Film: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="17" x2="22" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/></svg>,
  Folder: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
  HardDrive: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="12" x2="2" y2="12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/><line x1="6" y1="16" x2="6.01" y2="16"/><line x1="10" y1="16" x2="10.01" y2="16"/></svg>,
};

// Event types
const eventTypes = ["Weddings", "Brand Launches", "Influencer Meetups", "Corporate Gigs", "College Fests", "Concert Tours", "Nightlife Events", "Award Nights", "Store Openings", "Fashion Shows", "Private Gigs"];

const TopBanner = () => {
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    const q = query(ref(db, "offers"), orderByChild("active"), equalTo(true));
    const unsub = onValue(q, (snap) => {
      const data = snap.val();
      setOffers(data ? Object.values(data) : []);
    });
    return () => unsub();
  }, []);

  if (offers.length === 0) return null;

  return (
    <div className="bg-red-500 text-white text-xs md:text-sm text-center py-2 px-4 flex flex-col md:flex-row items-center justify-center gap-2 z-50 relative">
      {offers.map((offer, i) => (
        <span key={offer.id}>
          <strong>{offer.title}:</strong> {offer.description} 
          {offer.discount_code && <span className="ml-2 bg-black/20 px-2 py-1 rounded font-mono font-bold">{offer.discount_code}</span>}
          {i < offers.length - 1 && <span className="mx-2 hidden md:inline">•</span>}
        </span>
      ))}
    </div>
  );
};

// Header - Fixed logo to show full brand name
const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <TopBanner />
      <header className={`sticky-header ${scrolled ? "py-2" : "py-3"}`} data-testid="header">
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <img src="/logo.png" alt="ClickCutGo Logo" className="h-10 md:h-14 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          <a href="#portfolio" className="text-white/70 hover:text-white transition text-sm font-medium">Our Work</a>
          <a href="#packages" className="text-white/70 hover:text-white transition text-sm font-medium">Packages</a>
          <Link to="/track" className="text-white/70 hover:text-white transition text-sm font-medium">Track Booking</Link>
          <a href="#faq" className="text-white/70 hover:text-white transition text-sm font-medium">FAQ</a>
          <Link to="/join-creator" className="text-white/70 hover:text-white transition text-sm font-medium">Join as Creator</Link>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a href="https://wa.me/917675957990?text=Hi%2C%20I%20want%20to%20book%20ClickCutGo" target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm py-2 px-4">
            <span className="flex items-center gap-2"><Icons.WhatsApp />Chat</span>
          </a>
          <Link to="/book" className="btn-primary text-sm py-2 px-5">Book Now</Link>
        </div>

        <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <Icons.X /> : <Icons.Menu />}
        </button>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-black/95 border-t border-white/10">
            <nav className="flex flex-col p-4 gap-3">
              <a href="#portfolio" className="text-white/70 py-2">Our Work</a>
              <a href="#packages" className="text-white/70 py-2">Packages</a>
              <Link to="/track" className="text-white/70 py-2">Track Booking</Link>
              <a href="#faq" className="text-white/70 py-2">FAQ</a>
              <Link to="/join-creator" className="text-white/70 py-2">Join as Creator</Link>
              <Link to="/book" className="btn-primary text-center mt-2">Book Now</Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
    </>
  );
};

// Hero Section - Improved with prominent guarantee banner
const HeroSection = () => (
  <section className="relative min-h-[95vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${ASSETS.heroBg})` }} />
    <div className="absolute inset-0 bg-black/80" />
    <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black" />
    
    <div className="max-w-7xl mx-auto px-4 py-20 text-center relative z-10">
      {/* Live Badge */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        <span className="text-sm text-white/80">Now Live in Hyderabad</span>
        <span className="text-red-500"><Icons.Zap /></span>
        <span className="text-sm text-red-400 font-semibold">Limited Slots</span>
      </motion.div>

      {/* Main Headline */}
      <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl md:text-6xl lg:text-7xl font-black leading-tight mb-6">
        <span className="text-white">Instant Reel Creation</span><br />
        <span className="text-red-500">for Events in Hyderabad</span>
      </motion.h1>

      {/* Subheadline - Premium Design */}
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-8">
        <p className="text-xl md:text-2xl text-white/80 mb-4">
          Shoot. Edit. Deliver. <span className="text-red-400 font-bold">Before Your Event Ends.</span>
        </p>
      </motion.div>

      {/* CTAs */}
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
        <Link to="/book" className="btn-primary flex items-center justify-center gap-2 text-base px-8 py-4">
          <Icons.Camera />Book Creator Now<Icons.ArrowRight />
        </Link>
        <a href="#portfolio" className="btn-secondary flex items-center justify-center gap-2 px-8 py-4">
          <Icons.Play />Watch Our Work
        </a>
      </motion.div>


      {/* Trust badges */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-10 flex flex-wrap justify-center gap-6 text-white/50 text-sm">
        <div className="flex items-center gap-2"><Icons.Clock /><span>Same Day Delivery</span></div>
        <div className="flex items-center gap-2"><Icons.Camera /><span>Shot on iPhone</span></div>
        <div className="flex items-center gap-2"><Icons.Users /><span>50+ Events Covered</span></div>
      </motion.div>
    </div>
  </section>
);

// Event Marquee
const EventMarquee = () => (
  <div className="py-6 border-y border-white/5 overflow-hidden bg-black/50">
    <div className="marquee-container">
      <div className="marquee-content">
        {[...eventTypes, ...eventTypes].map((event, i) => (
          <span key={i} className="inline-flex items-center gap-4 mx-6 text-white/40 text-sm">
            {event}<span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
          </span>
        ))}
      </div>
    </div>
  </div>
);

// Portfolio Section
const DEFAULT_VIDEOS = [
  { url: "/videos/amr_converted.mp4", title: "A Night to Remember" },
  { url: "/videos/cafe_converted.mp4", title: "Brewed to Perfection" },
  { url: "/videos/wedding_converted.mp4", title: "Forever Begins Here" },
  { url: "/videos/baby_shower_converted.mp4", title: "Little One on the Way" }
];

const PortfolioSection = () => {
  const [videos, setVideos] = useState(DEFAULT_VIDEOS);

  useEffect(() => {
    const q = query(ref(db, "videos"), orderByChild("active"), equalTo(true));
    const unsub = onValue(q, (snap) => {
      const data = snap.val();
      if (data) {
        const arr = Object.values(data);
        if (arr.length > 0) setVideos(arr);
      }
    });
    return () => unsub();
  }, []);

  return (
  <section id="portfolio" className="section bg-gradient-to-b from-black via-red-950/10 to-black" data-testid="portfolio-section">
    <div className="max-w-7xl mx-auto px-4">
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
        <h2 className="text-3xl md:text-5xl font-black mb-4">Our <span className="text-red-500">Work</span></h2>
        <p className="text-white/60 text-lg">Real event reels we've delivered</p>
      </motion.div>

      <div className="flex overflow-x-auto gap-6 md:gap-8 pb-8 snap-x snap-mandatory w-full px-4 md:px-12" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {videos.map((video, i) => (
          <motion.div 
            key={i} 
            initial={{ opacity: 0, y: 30 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            transition={{ delay: i * 0.2 }} 
            className="relative shrink-0 snap-center aspect-[9/16] h-[450px] md:h-[550px] rounded-2xl overflow-hidden glass-card group"
          >
            <video 
              src={video.url.startsWith('http') ? video.url : process.env.PUBLIC_URL + video.url} 
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-4 left-4 right-4">
              <h4 className="text-white font-bold text-lg drop-shadow-lg">{video.title}</h4>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
  );
};

// How It Works
const HowItWorks = () => {
  const steps = [
    { num: 1, title: "Book in 30 Seconds", desc: "Pick your package. Lock your slot instantly." },
    { num: 2, title: "Creator Arrives On Time", desc: "iPhone ready. Energy matched to your event." },
    { num: 3, title: "Shot & Edited Live", desc: "Every frame captured and cut in real time." },
    { num: 4, title: "Reel Delivered Instantly", desc: "Instagram-ready 9:16 reel before you leave." }
  ];

  return (
    <section id="how" className="section">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-black mb-4">How It <span className="text-red-500">Works</span></h2>
          <p className="text-white/60 text-lg">From booking to viral reel. In minutes.</p>
        </motion.div>

        <div className="grid md:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <motion.div key={step.num} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="glass-card rounded-2xl p-6 text-center relative">
              {i < 3 && <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-red-500/50" />}
              <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
                <span className="text-red-500 font-bold text-xl">{step.num}</span>
              </div>
              <h3 className="text-lg font-bold mb-2">{step.title}</h3>
              <p className="text-white/60 text-sm">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Packages Section
const PackagesSection = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(ref(db, "packages"), orderByChild("active"), equalTo(true));
    const unsub = onValue(q, (snap) => {
      const data = snap.val();
      setPackages(data ? Object.values(data) : []);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  return (
    <section id="packages" className="section">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-black mb-4">
            <span className="text-white">Our Packages</span>
          </h2>
          <p className="text-white/60 text-lg">All packages include Hyderabad city travel.</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {[...packages.filter(p => p.category === "standard")].sort((a, b) => a.price - b.price).map((pkg, i) => {
            const discountedPrice = pkg.discount ? Math.round(pkg.price * (1 - pkg.discount / 100)) : null;
            return (
            <motion.div key={pkg.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className={`package-card ${pkg.popular ? "popular" : ""}`}>
              {pkg.popular && <span className="badge badge-popular absolute top-4 right-4">Most Popular</span>}
              {pkg.discount > 0 && <span className="badge absolute top-4 left-4" style={{background:'#16a34a',color:'white'}}>{pkg.discount_label || `${pkg.discount}% OFF`}</span>}
              <h4 className="text-xl font-bold mb-1">{pkg.name}</h4>
              {pkg.description && <p className="text-white/60 text-xs mb-4">{pkg.description}</p>}
              <div className="flex items-baseline gap-2 mb-6">
                {discountedPrice ? (
                  <>
                    <span className="text-3xl font-black text-red-500">₹{discountedPrice.toLocaleString('en-IN')}</span>
                    <span className="text-white/40 line-through text-lg">₹{pkg.price.toLocaleString('en-IN')}</span>
                  </>
                ) : (
                  <span className="text-3xl font-black text-red-500">₹{pkg.price.toLocaleString('en-IN')}</span>
                )}
              </div>
              <ul className="space-y-2 mb-6">
                {pkg.features.map((f, j) => <li key={j} className="flex items-center gap-2 text-white/70 text-sm"><span className="text-red-500"><Icons.Check /></span>{f}</li>)}
              </ul>
              <button onClick={() => navigate(`/book?package=${pkg.id}`)} className={`w-full ${pkg.popular ? "btn-primary" : "btn-secondary"}`}>Book Now</button>
            </motion.div>
            );
          })}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="package-card flex flex-col items-center justify-center text-center">
            <span className="text-3xl mb-4">🎯</span>
            <h4 className="text-xl font-bold mb-2">Custom Package</h4>
            <p className="text-white/50 text-sm mb-6">Need something different? Let's build a plan that fits your event perfectly.</p>
            <a href="https://wa.me/917675957990?text=Hi%2C%20I%20want%20a%20custom%20package%20for%20my%20event" target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick('custom_package')} className="w-full btn-secondary flex items-center justify-center gap-2">
              <Icons.WhatsApp />Chat With Us
            </a>
          </motion.div>
        </div>

        <h3 className="text-xl font-bold mb-6 text-white/80">Wedding Packages</h3>
        <div className="grid md:grid-cols-3 gap-6">
          {[...packages.filter(p => p.category === "wedding")].sort((a, b) => a.price - b.price).map((pkg, i) => {
            const discountedPrice = pkg.discount ? Math.round(pkg.price * (1 - pkg.discount / 100)) : null;
            return (
            <motion.div key={pkg.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className={`package-card ${pkg.premium ? "popular" : ""}`}>
              {pkg.premium && <span className="badge badge-popular absolute top-4 right-4">Premium</span>}
              {pkg.discount > 0 && <span className="badge absolute top-4 left-4" style={{background:'#16a34a',color:'white'}}>{pkg.discount_label || `${pkg.discount}% OFF`}</span>}
              <h4 className="text-lg font-bold mb-1">{pkg.name}</h4>
              {pkg.description && <p className="text-white/60 text-xs mb-4">{pkg.description}</p>}
              <div className="flex items-baseline gap-2 mb-4">
                {discountedPrice ? (
                  <>
                    <span className="text-2xl font-black text-red-500">₹{discountedPrice.toLocaleString('en-IN')}</span>
                    <span className="text-white/40 line-through text-base">₹{pkg.price.toLocaleString('en-IN')}</span>
                  </>
                ) : (
                  <span className="text-2xl font-black text-red-500">₹{pkg.price.toLocaleString('en-IN')}</span>
                )}
              </div>
              <ul className="space-y-2 mb-6">
                {pkg.features.map((f, j) => <li key={j} className="flex items-center gap-2 text-white/70 text-sm"><span className="text-red-500"><Icons.Check /></span>{f}</li>)}
              </ul>
              <button onClick={() => navigate(`/book?package=${pkg.id}`)} className="w-full btn-secondary">Book Now</button>
            </motion.div>
            );
          })}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="package-card flex flex-col items-center justify-center text-center">
            <span className="text-3xl mb-4">💍</span>
            <h4 className="text-xl font-bold mb-2">Custom Wedding</h4>
            <p className="text-white/50 text-sm mb-6">Multi-day coverage, cinematic reels — tell us your dream wedding vision.</p>
            <a href="https://wa.me/917675957990?text=Hi%2C%20I%20want%20a%20custom%20wedding%20package" target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick('custom_wedding')} className="w-full btn-secondary flex items-center justify-center gap-2">
              <Icons.WhatsApp />Chat With Us
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Add-Ons Section - NEW
const AddOnsSection = () => {
  const [addOns, setAddOns] = useState([]);

  useEffect(() => {
    const q = query(ref(db, "addons"), orderByChild("active"), equalTo(true));
    const unsub = onValue(q, (snap) => {
      const data = snap.val();
      setAddOns(data ? Object.values(data) : []);
    });
    return () => unsub();
  }, []);

  const getIcon = (iconName) => {
    const Icon = Icons[iconName] || Icons.Zap;
    return <Icon />;
  };

  return (
  <section className="section bg-gradient-to-b from-black via-red-950/5 to-black">
    <div className="max-w-4xl mx-auto px-4">
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass-card rounded-3xl p-8 md:p-10">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 text-red-500 mb-2">
            <Icons.Plus />
            <span className="font-bold">Power Add-Ons</span>
          </div>
          <h3 className="text-3xl md:text-4xl font-black text-red-500">₹1,250 Each</h3>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          {addOns.map((addon, i) => (
            <motion.div
              key={addon.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-red-500/30 transition"
            >
              <div className="text-red-500">{getIcon(addon.icon)}</div>
              <span className="font-medium text-sm md:text-base">{addon.name}</span>
            </motion.div>
          ))}
        </div>

        <p className="text-white/50 text-center text-sm italic">Add-ons can be selected during booking</p>

        {/* WhatsApp CTA */}
        <div className="mt-8 p-6 rounded-2xl bg-white/5 border border-white/10 text-center">
          <p className="text-white/70 mb-4">Not sure which plan? Talk to us — we'll pick for you.</p>
          <a 
            href="https://wa.me/917675957990?text=Hi%2C%20I%20need%20help%20choosing%20a%20package" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-full transition"
          >
            <Icons.WhatsApp />
            Ask on WhatsApp
          </a>
        </div>
      </motion.div>
    </div>
  </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [faqs, setFaqs] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    const q = query(ref(db, "faqs"), orderByChild("active"), equalTo(true));
    const unsub = onValue(q, (snap) => {
      const data = snap.val();
      setFaqs(data ? Object.values(data) : []);
    });
    return () => unsub();
  }, []);

  return (
    <section id="faq" className="section">
      <div className="max-w-3xl mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-black mb-4">Got <span className="text-red-500">Questions?</span></h2>
          <p className="text-white/60">We've got answers.</p>
        </motion.div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <motion.div key={faq.id || i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="glass-card rounded-xl overflow-hidden">
              <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full p-5 flex items-center justify-between text-left hover:bg-white/5 transition">
                <span className="font-semibold pr-4">{faq.question}</span>
                <span className="text-red-500">{openIndex === i ? <Icons.ChevronUp /> : <Icons.ChevronDown />}</span>
              </button>
              <AnimatePresence>
                {openIndex === i && (
                  <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
                    <p className="px-5 pb-5 text-white/60">{faq.answer}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section - Shows published customer feedback
const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const res = await axios.get(`${API}/feedback/published`);
        setTestimonials(res.data.feedback || []);
      } catch (err) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  if (loading) {
    return (
      <section className="section py-16 bg-black/40">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return null; // Hide if no reviews are published yet
  }

  return (
    <section className="section py-16 relative overflow-hidden bg-gradient-to-b from-black/20 to-black/60" id="testimonials">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-red-500/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="max-w-6xl mx-auto px-4 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }} 
          className="text-center mb-12"
        >
          <span className="px-3 py-1 rounded-full bg-red-500/10 text-red-400 text-xs font-semibold uppercase tracking-wider mb-3 inline-block">
            Customer Reviews
          </span>
          <h2 className="text-3xl md:text-4xl font-black mb-3">
            Loved by Hosts, <span className="text-red-500">Shared by Thousands</span>
          </h2>
          <p className="text-white/60 max-w-xl mx-auto text-base">
            See what our clients say about their instant reels experience.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, idx) => (
            <motion.div 
              key={t.id || idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
              className="glass-card rounded-2xl p-6 border border-white/5 flex flex-col justify-between hover:border-red-500/30 transition-all duration-300 group"
            >
              <div>
                <div className="flex items-center gap-1 mb-4 text-yellow-400">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Icons.Star key={i} filled={i < (t.rating || 5)} />
                  ))}
                </div>
                <p className="text-white/80 text-base italic mb-6 leading-relaxed">
                  "{t.message}"
                </p>
              </div>
              <div className="flex items-center justify-between border-t border-white/5 pt-4">
                <div>
                  <h4 className="font-bold text-white group-hover:text-red-400 transition-colors">{t.name}</h4>
                  <p className="text-white/40 text-xs capitalize">{t.event_type || "Event"}</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center text-red-400 text-xs font-bold">
                  {t.name ? t.name.charAt(0).toUpperCase() : "C"}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Feedback Form
const FeedbackSection = () => {
  const [form, setForm] = useState({ name: "", event_type: "", rating: 5, message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/feedback`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error("Failed to submit feedback");
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <section className="section">
        <div className="max-w-xl mx-auto px-4 text-center">
          <div className="glass-card rounded-2xl p-8">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4 text-green-400">
              <Icons.Check />
            </div>
            <h3 className="text-2xl font-bold mb-2">Thank You!</h3>
            <p className="text-white/60">Your feedback helps us improve.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section">
      <div className="max-w-xl mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-8">
          <h2 className="text-3xl font-black mb-2">Share Your <span className="text-red-500">Experience</span></h2>
          <p className="text-white/60">Help us get better</p>
        </motion.div>

        <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-6 space-y-4">
          <div>
            <label className="block text-white/60 text-sm mb-1">Your Name *</label>
            <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="input-field" placeholder="Your name" />
          </div>
          <div>
            <label className="block text-white/60 text-sm mb-1">Event Type</label>
            <select value={form.event_type} onChange={e => setForm({ ...form, event_type: e.target.value })} className="input-field">
              <option value="">Select event type</option>
              <option value="wedding">Wedding</option>
              <option value="corporate">Corporate</option>
              <option value="concert">Concert</option>
              <option value="college_fest">College Fest</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-white/60 text-sm mb-1">Rating</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} className={`p-2 ${form.rating >= n ? "text-yellow-400" : "text-white/30"}`}>
                  <Icons.Star filled={form.rating >= n} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-white/60 text-sm mb-1">Your Message</label>
            <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="input-field h-24 resize-none" placeholder="Share your experience..." />
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full">{loading ? "Submitting..." : "Submit Feedback"}</button>
        </form>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => (
  <section className="section">
    <div className="max-w-4xl mx-auto px-4 text-center">
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass-card rounded-3xl p-8 md:p-12 border border-red-500/20">
        <h2 className="text-3xl md:text-4xl font-black mb-4">Your Event Happens <span className="text-red-500">Once.</span></h2>
        <p className="text-white/60 text-lg mb-8">Make it unforgettable. Make it go viral.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/book" className="btn-primary flex items-center justify-center gap-2 px-8 py-4"><Icons.Camera />Book Creator Now</Link>
          <a href="https://wa.me/917675957990?text=Hi%2C%20I%20want%20to%20book%20ClickCutGo" target="_blank" rel="noopener noreferrer" className="btn-secondary flex items-center justify-center gap-2 px-8 py-4">
            <Icons.WhatsApp />Chat on WhatsApp
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

// Footer with social icons
const Footer = () => (
  <footer className="py-12 border-t border-white/5">
    <div className="max-w-7xl mx-auto px-4">
      <div className="grid md:grid-cols-4 gap-8 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <img src="/logo.png" alt="ClickCutGo Logo" className="h-8 md:h-10 w-auto object-contain" />
          </div>
          <p className="text-white/50 text-sm">Hyderabad's Go-To for Instant Event Reels</p>
        </div>
        <div>
          <h4 className="font-bold mb-4">Quick Links</h4>
          <div className="space-y-2 text-sm text-white/60">
            <Link to="/book" className="block hover:text-white transition">Book Now</Link>
            <Link to="/track" className="block hover:text-white transition">Track Booking</Link>
            <a href="#packages" className="block hover:text-white transition">Packages</a>
            <a href="#faq" className="block hover:text-white transition">FAQ</a>
            <Link to="/join-creator" className="block hover:text-white transition">Join as Creator</Link>
          </div>
        </div>
        <div>
          <h4 className="font-bold mb-4">Legal</h4>
          <div className="space-y-2 text-sm text-white/60">
            <Link to="/refund-policy" className="block hover:text-white transition">Refund Policy</Link>
            <Link to="/terms" className="block hover:text-white transition">Terms & Conditions</Link>
            <Link to="/privacy" className="block hover:text-white transition">Privacy Policy</Link>
          </div>
        </div>
        <div>
          <h4 className="font-bold mb-4">Connect With Us</h4>
          <div className="flex gap-4">
            <a href="https://instagram.com/clickcutgo" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:bg-red-500/20 hover:text-red-400 transition"><Icons.Instagram /></a>
            <a href="https://facebook.com/clickcutgo" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:bg-blue-500/20 hover:text-blue-400 transition"><Icons.Facebook /></a>
            <a href="https://linkedin.com/company/clickcutgo" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:bg-blue-600/20 hover:text-blue-500 transition"><Icons.LinkedIn /></a>
            <a href="https://wa.me/917675957990" target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick('footer')} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:bg-green-500/20 hover:text-green-400 transition"><Icons.WhatsApp /></a>
          </div>
        </div>
      </div>
      <div className="pt-8 border-t border-white/5 text-center text-white/40 text-sm">
        © 2026 ClickCutGo. All rights reserved.
      </div>
    </div>
  </footer>
);

// Chatbot
const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const options = [
    { label: "Book Now", icon: <Icons.Calendar />, action: () => { navigate("/book"); setOpen(false); } },
    { label: "View Pricing", icon: <Icons.Package />, action: () => { window.location.href = "/#packages"; setOpen(false); } },
    { label: "Talk to Human", icon: <Icons.Phone />, action: () => { trackPhoneCall('chatbot'); window.open("https://wa.me/917675957990?text=Hi%2C%20I%20need%20help", "_blank"); } },
    { label: "Become Creator", icon: <Icons.Camera />, action: () => { navigate("/join-creator"); setOpen(false); } },
  ];

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.9 }} className="absolute bottom-16 right-0 w-72 glass-card rounded-2xl p-4 mb-2 border border-white/10">
            <h4 className="font-bold mb-3 text-lg">How can we help?</h4>
            <div className="space-y-2">
              {options.map((opt, i) => (
                <button key={i} onClick={opt.action} className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-red-500/10 hover:border-red-500/30 border border-transparent transition text-left">
                  <span className="text-red-500">{opt.icon}</span>
                  <span className="font-medium">{opt.label}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <button onClick={() => setOpen(!open)} className="w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-red-700 text-white flex items-center justify-center shadow-lg shadow-red-500/40 hover:shadow-red-500/60 transition">
        {open ? <Icons.X /> : <Icons.MessageCircle />}
      </button>
    </div>
  );
};

// Home Page
const HomePage = () => (
  <div data-testid="home-page">
    <Header />
    <HeroSection />
    <EventMarquee />
    <PortfolioSection />
    <HowItWorks />
    <PackagesSection />
    <AddOnsSection />
    <FAQSection />
    <TestimonialsSection />
    <FeedbackSection />
    <CTASection />
    <Footer />
    <Chatbot />
  </div>
);

// Booking Page with Add-ons
const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselectedPackage = searchParams.get("package") || "creator_pro";

  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", event_type: "", event_date: "", event_time: "",
    venue_name: "", venue_address: "", package_type: preselectedPackage,
    addons: [], message: "", selected_songs: []
  });
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);
  const [bookingResponse, setBookingResponse] = useState(null);
  const [songs, setSongs] = useState([]);
  
  const [packages, setPackages] = useState([]);
  const [addOns, setAddOns] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/packages`),
      axios.get(`${API}/addons`)
    ]).then(([pkgRes, addonRes]) => {
      setPackages(pkgRes.data);
      setAddOns(addonRes.data);
      setDataLoading(false);
    }).catch(err => {
      console.error(err);
      setDataLoading(false);
    });
  }, []);

  const selectedPackage = packages.find(p => p.id === formData.package_type);
  const addonPrice = formData.addons.reduce((total, addonId) => {
    const addon = addOns.find(a => a.id === addonId);
    return total + (addon ? addon.price : 0);
  }, 0);
  const totalPrice = (selectedPackage?.price || 0) + addonPrice;
  const advanceAmount = Math.round(totalPrice * 0.5);

  useEffect(() => {
    if (formData.event_type) {
      axios.get(`${API}/songs/${formData.event_type}`).then(res => setSongs(res.data.songs || [])).catch(() => {});
    }
  }, [formData.event_type]);

  const toggleAddon = (addonId) => {
    setFormData(prev => {
      const newAddons = prev.addons.includes(addonId)
        ? prev.addons.filter(a => a !== addonId)
        : [...prev.addons, addonId];
      const newAddonPrice = newAddons.reduce((total, id) => {
        const addon = addOns.find(a => a.id === id);
        return total + (addon ? addon.price : 0);
      }, 0);
      trackAddonsSelected(newAddons, newAddonPrice);
      return { ...prev, addons: newAddons };
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      trackBookingInquiry(formData, formData.package_type);
      const res = await axios.post(`${API}/bookings/inquiry`, { ...formData, source: "website" });
      trackBookingSubmitted(res.data.booking_id, totalPrice, formData.package_type, formData.event_type);
      setBookingResponse(res.data);
      setStep(5);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    }
    setLoading(false);
  };

  const handlePayment = async () => {
    if (!bookingResponse) return;
    setLoading(true);
    setError("");
    try {
      const orderRes = await axios.post(`${API}/payments/create-order`, { booking_id: bookingResponse.booking_id, payment_type: "advance" });
      const { order_id, amount_paise, key_id, name, phone, email } = orderRes.data;
      trackPaymentInitiated(bookingResponse.booking_id, amount_paise / 100);

      if (window.Razorpay && key_id) {
        const options = {
          key: key_id, amount: amount_paise, currency: "INR",
          name: "ClickCutGo", description: `Booking: ${bookingResponse.booking_id}`,
          order_id: order_id,
          handler: async function(response) {
            try {
              await axios.post(`${API}/payments/verify`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                booking_id: bookingResponse.booking_id
              });
              trackPaymentCompleted(bookingResponse.booking_id, amount_paise / 100, 'razorpay');
              navigate(`/booking-success?id=${bookingResponse.booking_id}`);
            } catch (err) {
              trackPaymentFailed(bookingResponse.booking_id, "Payment verification failed");
              setError("Payment verification failed. Please contact support.");
            }
          },
          prefill: { name, contact: phone, email },
          theme: { color: "#dc2626" },
          modal: {
            ondismiss: function() {
              setError("Payment cancelled. Please try again.");
            }
          }
        };
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
          trackPaymentFailed(bookingResponse.booking_id, response.error.description);
          setError(`Payment failed: ${response.error.description}`);
        });
        rzp.open();
      } else {
        trackPaymentInitiated(bookingResponse.booking_id, amount_paise / 100);
        window.open(`https://wa.me/917675957990?text=Hi%2C%20I%20want%20to%20pay%20for%20booking%20${bookingResponse.booking_id}`, "_blank");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create payment order. Please try WhatsApp.");
    }
    setLoading(false);
  };

  const toggleSong = (song) => {
    setFormData(prev => ({
      ...prev,
      selected_songs: prev.selected_songs.includes(song.title)
        ? prev.selected_songs.filter(s => s !== song.title)
        : [...prev.selected_songs, song.title]
    }));
  };

  return (
    <div className="min-h-screen" data-testid="booking-page">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 md:gap-4 mb-12 overflow-x-auto">
          {["Package", "Add-ons", "Details", "Songs", "Payment"].map((s, i) => (
            <div key={i} className="flex items-center">
              <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-sm ${step >= i + 1 ? "bg-red-500 text-white" : "bg-white/10 text-white/40"}`}>{i + 1}</div>
              {i < 4 && <div className={`w-4 md:w-8 h-0.5 ${step > i + 1 ? "bg-red-500" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Package */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h1 className="text-2xl font-black text-center mb-8">Select Your Package</h1>
            <div className="grid gap-3 mb-8">
              {packages.map((pkg) => (
                <div key={pkg.id} onClick={() => setFormData(prev => ({ ...prev, package_type: pkg.id }))}
                  className={`glass-card rounded-xl p-4 cursor-pointer transition flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 relative ${formData.package_type === pkg.id ? "ring-2 ring-red-500 bg-red-500/10 shadow-[0_0_20px_rgba(220,38,38,0.25)] border-transparent" : "hover:border-white/20"}`}>
                  <div className="flex items-center gap-4 w-full sm:w-auto">
                    {/* Selection circle with checkmark */}
                    <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center border transition ${formData.package_type === pkg.id ? "bg-red-500 border-red-500 text-white" : "border-white/20"}`}>
                      {formData.package_type === pkg.id && <Icons.Check />}
                    </div>
                    <div>
                      <h3 className="font-bold text-base md:text-lg">{pkg.name}</h3>
                      <p className="text-white/60 text-xs md:text-sm">{pkg.features.slice(0, 2).join(" • ")}</p>
                    </div>
                  </div>
                  <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center w-full sm:w-auto border-t sm:border-t-0 border-white/5 pt-3 sm:pt-0 mt-2 sm:mt-0">
                    <span className="text-xl md:text-2xl font-black text-red-500">₹{pkg.price.toLocaleString()}</span>
                    {pkg.popular && <span className="badge badge-popular text-xs mt-1 sm:mt-0 sm:ml-2">Popular</span>}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setStep(2)} className="btn-primary w-full py-4 text-base md:text-lg font-bold">Continue to Add-ons</button>
          </motion.div>
        )}

        {/* Step 2: Add-ons */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h1 className="text-2xl font-black text-center mb-2">Power Add-Ons</h1>
            <p className="text-center text-white/60 mb-8">₹1,250 each • Select as many as you need</p>
            
            <div className="grid grid-cols-2 gap-4 mb-8">
              {addOns.map((addon) => {
                const Icon = Icons[addon.icon] || Icons.Zap;
                return (
                  <div
                    key={addon.id}
                    onClick={() => toggleAddon(addon.id)}
                    className={`glass-card rounded-xl p-4 cursor-pointer transition flex items-center gap-3 ${formData.addons.includes(addon.id) ? "border-red-500 bg-red-500/10" : ""}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${formData.addons.includes(addon.id) ? "bg-red-500 text-white" : "bg-white/5 text-red-500"}`}>
                      {formData.addons.includes(addon.id) ? <Icons.Check /> : <Icon />}
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">{addon.name}</h4>
                      <p className="text-white/50 text-xs">₹{addon.price.toLocaleString()}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            <div className="glass-card rounded-xl p-4 mb-6">
              <div className="flex justify-between mb-2"><span className="text-white/60">Package</span><span>₹{selectedPackage?.price.toLocaleString()}</span></div>
              {addonPrice > 0 && <div className="flex justify-between mb-2"><span className="text-white/60">Add-ons ({formData.addons.length})</span><span>₹{addonPrice.toLocaleString()}</span></div>}
              <div className="flex justify-between pt-3 border-t border-white/10"><span className="font-bold">Total</span><span className="text-xl font-black text-red-500">₹{totalPrice.toLocaleString()}</span></div>
              <p className="text-white/40 text-sm mt-2">Pay ₹{advanceAmount.toLocaleString()} advance to confirm</p>
            </div>

            <div className="flex gap-4">
              <button onClick={() => setStep(1)} className="btn-secondary flex-1">Back</button>
              <button onClick={() => setStep(3)} className="btn-primary flex-1">Continue</button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Event Details */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h1 className="text-2xl font-black text-center mb-8">Event Details</h1>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="block text-white/60 text-sm mb-1">Your Name *</label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="input-field" placeholder="Full name" /></div>
                <div><label className="block text-white/60 text-sm mb-1">Phone *</label>
                  <input type="tel" required value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="input-field" placeholder="+91 98765 43210" /></div>
              </div>
              <div><label className="block text-white/60 text-sm mb-1">Email</label>
                <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="input-field" placeholder="you@example.com" /></div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="block text-white/60 text-sm mb-1">Event Type *</label>
                  <select required value={formData.event_type} onChange={e => setFormData({ ...formData, event_type: e.target.value })} className="input-field">
                    <option value="">Select event type</option>
                    <option value="wedding">Wedding</option>
                    <option value="corporate">Corporate Event</option>
                    <option value="college_fest">College Fest</option>
                    <option value="concert">Concert</option>
                    <option value="birthday">Birthday Party</option>
                    <option value="other">Other</option>
                  </select></div>
                <div><label className="block text-white/60 text-sm mb-1">Event Date *</label>
                  <input type="date" required value={formData.event_date} onChange={e => setFormData({ ...formData, event_date: e.target.value })} className="input-field" min={new Date().toISOString().split("T")[0]} /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="block text-white/60 text-sm mb-1">Event Time</label>
                  <input type="time" value={formData.event_time} onChange={e => setFormData({ ...formData, event_time: e.target.value })} className="input-field" /></div>
                <div><label className="block text-white/60 text-sm mb-1">Venue Name</label>
                  <input type="text" value={formData.venue_name} onChange={e => setFormData({ ...formData, venue_name: e.target.value })} className="input-field" placeholder="Hotel, Hall, etc." /></div>
              </div>
              <div className="flex gap-4 mt-6">
                <button type="button" onClick={() => setStep(2)} className="btn-secondary flex-1">Back</button>
                <button type="button" onClick={() => { if (formData.name && formData.phone && formData.event_type && formData.event_date) setStep(4); else toast.error("Please fill required fields"); }} className="btn-primary flex-1">Continue</button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 4: Song Selection */}
        {step === 4 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h1 className="text-2xl font-black text-center mb-2">Select Songs for Your Reel</h1>
            <p className="text-white/60 text-center mb-8">AI-powered recommendations based on your event</p>
            
            {songs.length > 0 ? (
              <div className="grid gap-3 mb-8">
                {songs.map((song, i) => (
                  <div key={i} onClick={() => toggleSong(song)}
                    className={`glass-card rounded-xl p-4 cursor-pointer transition flex items-center gap-4 ${formData.selected_songs.includes(song.title) ? "border-red-500 bg-red-500/10" : ""}`}>
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${formData.selected_songs.includes(song.title) ? "bg-red-500 text-white" : "bg-red-500/10 text-red-500"}`}>
                      <Icons.Music />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold">{song.title}</h4>
                      <p className="text-white/60 text-sm">{song.artist} • {song.mood}</p>
                    </div>
                    {formData.selected_songs.includes(song.title) && <span className="text-green-400"><Icons.Check /></span>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="glass-card rounded-xl p-8 text-center mb-8">
                <div className="text-red-500 mb-2"><Icons.Music /></div>
                <p className="text-white/60">Song recommendations will appear based on event type</p>
              </div>
            )}
            
            <div className="flex gap-4">
              <button type="button" onClick={() => setStep(3)} className="btn-secondary flex-1">Back</button>
              <button type="button" onClick={handleSubmit} disabled={loading} className="btn-primary flex-1">
                {loading ? "Creating Booking..." : "Proceed to Payment"}
              </button>
            </div>
            {error && <p className="text-red-400 text-center mt-4">{error}</p>}
          </motion.div>
        )}

        {/* Step 5: Payment */}
        {step === 5 && bookingResponse && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6 text-green-400">
              <Icons.Check />
            </div>
            <h1 className="text-2xl font-black mb-4">Booking Created!</h1>
            <p className="text-white/60 mb-8">Booking ID: <span className="text-red-500 font-mono">{bookingResponse.booking_id}</span></p>
            
            <div className="glass-card rounded-xl p-6 mb-8 text-left max-w-md mx-auto">
              <div className="flex justify-between mb-2"><span className="text-white/60">Package</span><span>₹{selectedPackage?.price.toLocaleString()}</span></div>
              {addonPrice > 0 && <div className="flex justify-between mb-2"><span className="text-white/60">Add-ons</span><span>₹{addonPrice.toLocaleString()}</span></div>}
              <div className="flex justify-between mb-4 pt-2 border-t border-white/10"><span className="text-white/60">Total Amount</span><span className="font-bold">₹{bookingResponse.total_price.toLocaleString()}</span></div>
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                <div className="flex justify-between"><span className="font-semibold">Pay Now (50%)</span><span className="text-2xl font-black text-red-500">₹{bookingResponse.advance_amount.toLocaleString()}</span></div>
              </div>
              <p className="text-white/40 text-sm mt-4 text-center">⚠️ Booking confirmed only after payment</p>
            </div>
            
            {error && <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 mb-4 max-w-md mx-auto">{error}</div>}
            
            <div className="flex flex-col gap-4 max-w-md mx-auto">
              <button onClick={handlePayment} disabled={loading} className="btn-primary py-4">
                {loading ? "Processing..." : `Pay ₹${bookingResponse.advance_amount.toLocaleString()} Now`}
              </button>
              <a href={`https://wa.me/917675957990?text=Hi%2C%20I%20created%20booking%20${bookingResponse.booking_id}%20and%20need%20help%20with%20payment`} target="_blank" rel="noopener noreferrer" className="btn-secondary flex items-center justify-center gap-2">
                <Icons.WhatsApp />Need Help? Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Booking Success
const BookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center p-8">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-24 h-24 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-6 text-white">
          <Icons.Check />
        </motion.div>
        <h1 className="text-3xl font-black mb-4">Payment Successful!</h1>
        <p className="text-white/60 mb-2">Your booking is now confirmed</p>
        <p className="text-2xl font-mono text-red-500 mb-8">{searchParams.get("id")}</p>
        <p className="text-white/60 mb-8">We'll send you all details on WhatsApp shortly.</p>
        <Link to="/" className="btn-primary">Back to Home</Link>
      </div>
    </div>
  );
};

// Join Creator Page
const JoinCreatorPage = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", instagram: "", city: "", experience_years: 1, portfolio_links: [], about: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linkInput, setLinkInput] = useState("");

  const addLink = () => {
    if (linkInput) {
      setForm({ ...form, portfolio_links: [...form.portfolio_links, linkInput] });
      setLinkInput("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/creators/apply`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-8 max-w-md">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6 text-green-400">
            <Icons.Check />
          </div>
          <h1 className="text-2xl font-black mb-4">Application Submitted!</h1>
          <p className="text-white/60 mb-8">We'll review your application and get back to you within 48 hours.</p>
          <Link to="/" className="btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <h1 className="text-3xl font-black mb-2">Join as <span className="text-red-500">Creator</span></h1>
          <p className="text-white/60">Elite creators. Top-tier gigs. Premium pay.</p>
        </motion.div>

        <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="block text-white/60 text-sm mb-1">Full Name *</label>
              <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="input-field" /></div>
            <div><label className="block text-white/60 text-sm mb-1">Phone *</label>
              <input type="tel" required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="input-field" /></div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="block text-white/60 text-sm mb-1">Email *</label>
              <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="input-field" /></div>
            <div><label className="block text-white/60 text-sm mb-1">Instagram Handle</label>
              <input type="text" value={form.instagram} onChange={e => setForm({ ...form, instagram: e.target.value })} className="input-field" placeholder="@username" /></div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="block text-white/60 text-sm mb-1">City *</label>
              <input type="text" required value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="input-field" /></div>
            <div><label className="block text-white/60 text-sm mb-1">Years of Experience *</label>
              <input type="number" min="0" required value={form.experience_years} onChange={e => setForm({ ...form, experience_years: parseInt(e.target.value) })} className="input-field" /></div>
          </div>
          <div>
            <label className="block text-white/60 text-sm mb-1">Portfolio Links</label>
            <div className="flex gap-2 mb-2">
              <input type="url" value={linkInput} onChange={e => setLinkInput(e.target.value)} className="input-field flex-1" placeholder="https://..." />
              <button type="button" onClick={addLink} className="btn-secondary px-4">Add</button>
            </div>
            {form.portfolio_links.length > 0 && (
              <div className="flex flex-wrap gap-2">{form.portfolio_links.map((link, i) => <span key={i} className="badge bg-white/10 text-sm truncate max-w-[200px]">{link}</span>)}</div>
            )}
          </div>
          <div>
            <label className="block text-white/60 text-sm mb-1">About Yourself</label>
            <textarea value={form.about} onChange={e => setForm({ ...form, about: e.target.value })} className="input-field h-24 resize-none" placeholder="Tell us about your experience..." />
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full">{loading ? "Submitting..." : "Submit Application"}</button>
        </form>
      </div>
    </div>
  );
};

// Static legal content - no backend needed
const LEGAL_CONTENT = {
  "refund-policy": {
    title: "Refund Policy",
    sections: [
      {
        heading: "1. Cancellation by Customer",
        items: [
          { sub: "More than 7 days before event:", points: ["Full refund of advance payment", "No cancellation charges"] },
          { sub: "3–7 days before event:", points: ["50% refund of advance payment", "50% cancellation charge applies"] },
          { sub: "Less than 3 days before event:", points: ["No refund available", "Full advance amount is non-refundable"] },
        ]
      },
      {
        heading: "2. Service Quality Issues",
        items: [
          { points: ["Contact us within 24 hours of delivery", "We'll review and offer re-edit or partial refund", "Decision made within 48 hours"] }
        ]
      },
      {
        heading: "3. Refund Processing",
        items: [
          { points: ["Refunds processed to original payment method", "UPI refunds: 24–48 hours", "Card refunds: 5–7 business days"] }
        ]
      },
      {
        heading: "4. Non-Refundable Items",
        items: [
          { points: ["Completed and delivered services", "Add-ons already utilized", "Travel charges for out-of-city bookings"] }
        ]
      },
    ],
    contact: "For refund requests: +91 7675957990 | support@clickcutgo.com | wa.me/917675957990"
  },
  "terms": {
    title: "Terms & Conditions",
    sections: [
      {
        heading: "1. Services",
        items: [{ points: ["On-location video shooting", "Real-time editing", "Same-day delivery of Instagram-ready reels"] }]
      },
      {
        heading: "2. Booking & Payment",
        items: [{ points: ["50% advance payment required to confirm booking", "Remaining 50% due on event day or upon delivery", "All prices include GST", "Hyderabad city travel included; outstation charges extra"] }]
      },
      {
        heading: "3. Client Responsibilities",
        items: [{ points: ["Provide accurate event details", "Ensure venue access for our creator", "Inform us of any changes 48 hours prior", "Ensure cooperation of event guests for footage"] }]
      },
      {
        heading: "4. Our Commitments",
        items: [{ points: ["Professional, on-time creator arrival", "Quality 4K vertical video content", "Real-time editing and delivery"] }]
      },
      {
        heading: "5. Content Usage",
        items: [{ points: ["Client owns delivered content", "ClickCutGo may use content for portfolio/marketing", "Client can opt-out of portfolio usage"] }]
      },
      {
        heading: "6. Limitation of Liability",
        items: [{ points: ["Maximum liability limited to booking amount", "Not liable for force majeure events", "Not responsible for third-party platform issues"] }]
      },
      {
        heading: "7. Intellectual Property",
        items: [{ points: ["ClickCutGo branding included in standard packages", "Raw footage available as add-on", "No resale of services without permission"] }]
      },
    ],
    contact: "Contact us: +91 7675957990 | support@clickcutgo.com"
  },
  "privacy-policy": {
    title: "Privacy Policy",
    sections: [
      {
        heading: "1. Information We Collect",
        items: [{ points: ["Personal information (name, email, phone number) when you book or sign up", "Event details (date, location, type) necessary for our services", "Usage data on how you interact with our website"] }]
      },
      {
        heading: "2. How We Use Your Information",
        items: [{ points: ["To provide, operate, and maintain our services", "To process transactions and send related information (e.g., confirmations, invoices)", "To communicate with you regarding your bookings, including customer support"] }]
      },
      {
        heading: "3. Information Sharing",
        items: [{ points: ["We do not sell your personal data to third parties", "Information is shared with our creators only as necessary to fulfill your booking", "Data may be disclosed if required by law or to protect our rights"] }]
      },
      {
        heading: "4. Data Security",
        items: [{ points: ["We implement industry-standard security measures to protect your data", "Payment information is processed securely through Razorpay and not stored on our servers"] }]
      }
    ],
    contact: "For privacy inquiries: support@clickcutgo.com"
  }
};

// Legal Pages
const LegalPage = ({ type }) => {
  const data = LEGAL_CONTENT[type];
  if (!data) return null;

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-3xl font-black mb-2">{data.title}</h1>
        <p className="text-white/40 text-sm mb-10">Last Updated: January 2025</p>
        <div className="space-y-8">
          {data.sections.map((section, i) => (
            <div key={i}>
              <h2 className="text-lg font-bold mb-3 text-white">{section.heading}</h2>
              {section.items.map((item, j) => (
                <div key={j} className="mb-3">
                  {item.sub && <p className="text-white/70 font-semibold mb-1">{item.sub}</p>}
                  <ul className="space-y-1 pl-4">
                    {item.points.map((pt, k) => (
                      <li key={k} className="text-white/60 text-sm flex items-start gap-2">
                        <span className="text-red-500 mt-1">•</span>{pt}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
          <div className="pt-6 border-t border-white/10 text-white/40 text-sm">{data.contact}</div>
        </div>
        <div className="mt-10">
          <Link to="/" className="text-red-500 hover:underline">← Back to Home</Link>
        </div>
      </div>
    </div>
  );
};


// Waitlist Page
const WaitlistPage = () => {
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref");
  const [form, setForm] = useState({ name: "", email: "", referral_code: referralCode || "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/waitlist/signup`, form);
      if (res.data.success) {
        setResult(res.data);
        setSuccess(true);
      } else {
        setError(res.data.message || "Failed to join waitlist");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    }
    setLoading(false);
  };

  const checkStats = async (code) => {
    try {
      const res = await axios.get(`${API}/waitlist/stats/${code}`);
      setStats(res.data);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    if (result?.referral_code) {
      checkStats(result.referral_code);
    }
  }, [result]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (success && result) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)" }}>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-2xl w-full glass-card rounded-3xl p-8 md:p-12 text-center">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6 text-green-400">
            <Icons.Check />
          </div>
          
          <h1 className="text-3xl md:text-4xl font-black mb-4">
            Welcome to the <span className="text-red-500">ClickCutGo</span> Waitlist!
          </h1>
          
          <p className="text-white/60 mb-8">
            You're in! Get early access to instant event reels in Hyderabad.
          </p>

          {stats && (
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="glass-card rounded-xl p-6">
                <p className="text-white/60 text-sm mb-1">Your Position</p>
                <p className="text-4xl font-black text-red-500">#{stats.position}</p>
              </div>
              <div className="glass-card rounded-xl p-6">
                <p className="text-white/60 text-sm mb-1">Referrals</p>
                <p className="text-4xl font-black text-green-400">{stats.referrals}</p>
              </div>
            </div>
          )}

          <div className="glass-card rounded-2xl p-6 mb-6 text-left">
            <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
              <Icons.Users />
              Share & Move Up Faster
            </h3>
            <p className="text-white/70 mb-4 text-sm">
              For every friend who joins with your code, you both move up the waitlist!
            </p>
            
            <div className="bg-black/30 rounded-xl p-4 mb-4">
              <p className="text-white/50 text-xs mb-2">Your Referral Code</p>
              <div className="flex items-center justify-between">
                <code className="text-2xl font-mono font-black text-red-500 tracking-wider">{result.referral_code}</code>
                <button onClick={() => copyToClipboard(result.referral_code)} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition">
                  📋
                </button>
              </div>
            </div>

            <div className="bg-black/30 rounded-xl p-4">
              <p className="text-white/50 text-xs mb-2">Your Referral Link</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={result.referral_url}
                  readOnly
                  className="input-field text-sm flex-1"
                />
                <button onClick={() => copyToClipboard(result.referral_url)} className="btn-secondary text-sm px-4 py-2">
                  Copy
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={`https://wa.me/?text=Join%20me%20on%20the%20ClickCutGo%20waitlist%20for%20instant%20event%20reels!%20Use%20my%20code:%20${result.referral_code}%20%E2%9E%A1%EF%B8%8F%20${encodeURIComponent(result.referral_url)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary flex items-center justify-center gap-2 flex-1"
            >
              <Icons.WhatsApp />
              Share on WhatsApp
            </a>
            <Link to="/" className="btn-secondary flex-1">
              Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)" }}>
      <div className="max-w-xl w-full">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center mb-6">
            <img src="/logo.png" alt="ClickCutGo Logo" className="h-12 w-auto object-contain" />
          </Link>

          <div className="mb-4">
            <span className="px-4 py-2 rounded-full bg-red-500/20 text-red-400 text-sm font-bold">
              🚀 Join the Waitlist
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-black mb-4">
            Be the First to <span className="text-red-500">Get Instant Reels</span>
          </h1>
          <p className="text-white/60 text-lg">
            Join the waitlist for early access to the fastest event videography service in Hyderabad.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card rounded-3xl p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-white/60 text-sm mb-2">Your Name *</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="input-field"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Email Address *</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="input-field"
                placeholder="you@example.com"
              />
            </div>

            {referralCode && (
              <div className="glass-card rounded-xl p-4 bg-green-500/10 border border-green-500/30">
                <p className="text-green-400 text-sm flex items-center gap-2">
                  <Icons.Check />
                  Referral code applied: <code className="font-mono font-bold">{referralCode}</code>
                </p>
              </div>
            )}

            {!referralCode && (
              <div>
                <label className="block text-white/60 text-sm mb-2">Referral Code (Optional)</label>
                <input
                  type="text"
                  value={form.referral_code}
                  onChange={e => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                  className="input-field"
                  placeholder="Enter code"
                />
              </div>
            )}

            {error && (
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? "Joining..." : "Join Waitlist"}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-white/40 text-sm">
              Already have an account?{" "}
              <Link to="/" className="text-red-500 hover:underline">
                Back to Home
              </Link>
            </p>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-8 text-center">
          <div className="flex items-center justify-center gap-6 text-white/40 text-sm">
            <div className="flex items-center gap-2">
              <Icons.Users />
              <span>500+ on waitlist</span>
            </div>
            <div className="flex items-center gap-2">
              <Icons.Zap />
              <span>Launching Soon</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [creators, setCreators] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, bookingsRes, leadsRes, feedbackRes, creatorsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/bookings?limit=50`),
        axios.get(`${API}/leads?limit=50`),
        axios.get(`${API}/feedback`),
        axios.get(`${API}/creators/applications`)
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data.bookings);
      setLeads(leadsRes.data.leads);
      setFeedback(feedbackRes.data.feedback);
      setCreators(creatorsRes.data.applications);
    } catch (err) {
      // ignore
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status?status=${status}`);
      fetchData();
    } catch (err) {
      // ignore
    }
  };

  const approveCreator = async (id) => {
    try {
      const res = await axios.put(`${API}/admin/creators/${id}/approve`);
      toast.success(`Creator approved! Temp password: ${res.data.temp_password}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const togglePublishFeedback = async (id) => {
    try {
      await axios.put(`${API}/admin/feedback/${id}/publish`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update feedback status");
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "-";

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: <Icons.Dashboard /> },
    { id: "bookings", label: "Bookings", icon: <Icons.Package /> },
    { id: "leads", label: "Leads", icon: <Icons.Users /> },
    { id: "feedback", label: "Feedback", icon: <Icons.MessageCircle /> },
    { id: "creators", label: "Creators", icon: <Icons.Camera /> },
    { id: "content", label: "Content", icon: <Icons.Folder /> },
  ];

  return (
    <div className="min-h-screen flex">
      <aside className="admin-sidebar hidden md:block">
        <div className="p-6">
          <Link to="/" className="flex items-center">
            <img src="/logo.png" alt="ClickCutGo Logo" className="h-9 w-auto object-contain" />
          </Link>
          <p className="text-white/40 text-sm mt-1">Admin Panel</p>
        </div>
        <nav className="mt-4">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`admin-nav-item w-full ${activeTab === tab.id ? "active" : ""}`}>
              {tab.icon}{tab.label}
            </button>
          ))}
        </nav>
      </aside>

      <main className="flex-1 p-6 md:p-8">
        <div className="md:hidden flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`p-2 rounded-lg flex items-center gap-2 text-sm whitespace-nowrap ${activeTab === tab.id ? "bg-red-500/20 text-red-500" : "text-white/60"}`}>
              {tab.icon}{tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : !stats ? (
          <div className="flex items-center justify-center h-64 text-red-500 bg-white/5 p-6 rounded-xl border border-red-500/30">
             Failed to load dashboard data. This might be a temporary server issue or a CORS security block. Please try again.
          </div>
        ) : (
          <>
            {activeTab === "dashboard" && stats && (
              <div>
                <h1 className="text-2xl font-black mb-6">Dashboard</h1>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="stat-card"><p className="text-white/60 text-sm">Today's Events</p><p className="text-3xl font-black">{stats.today.bookings}</p></div>
                  <div className="stat-card"><p className="text-white/60 text-sm">Total Bookings</p><p className="text-3xl font-black">{stats.bookings.total}</p></div>
                  <div className="stat-card"><p className="text-white/60 text-sm">Total Revenue</p><p className="text-3xl font-black text-red-500">₹{stats.revenue.total.toLocaleString()}</p></div>
                  <div className="stat-card"><p className="text-white/60 text-sm">New Leads</p><p className="text-3xl font-black">{stats.leads.new}</p></div>
                </div>
              </div>
            )}

            {activeTab === "content" && <ContentManager />}

            {activeTab === "bookings" && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h1 className="text-2xl font-black">Bookings</h1>
                  <button onClick={fetchData} className="btn-secondary text-sm">Refresh</button>
                </div>
                <div className="space-y-3">
                  {bookings.map((b) => (
                    <div key={b.id} className="glass-card rounded-xl p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <p className="font-mono text-red-400 text-sm">{b.booking_id}</p>
                          <h4 className="font-bold">{b.name}</h4>
                          <p className="text-white/60 text-sm">{b.event_type} • {formatDate(b.event_date)}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-bold">₹{b.total_price?.toLocaleString()}</p>
                            <p className={`text-sm ${b.payment_status === "pending" ? "text-yellow-400" : "text-green-400"}`}>{b.payment_status}</p>
                          </div>
                          <select value={b.status} onChange={(e) => updateBookingStatus(b.booking_id, e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
                            <option value="inquiry">Inquiry</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="assigned">Assigned</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "leads" && (
              <div>
                <h1 className="text-2xl font-black mb-6">Leads</h1>
                <div className="space-y-3">
                  {leads.map((l) => (
                    <div key={l.id} className="glass-card rounded-xl p-4 flex justify-between items-center">
                      <div>
                        <h4 className="font-bold">{l.name}</h4>
                        <p className="text-white/60 text-sm">{l.phone} • {l.event_type || "Unknown"}</p>
                      </div>
                      <a href={`https://wa.me/91${l.phone?.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20">
                        <Icons.WhatsApp />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "feedback" && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h1 className="text-2xl font-black">Feedback</h1>
                  <button onClick={fetchData} className="btn-secondary text-sm">Refresh</button>
                </div>
                <div className="space-y-3">
                  {feedback.map((f) => (
                    <div key={f.id} className="glass-card rounded-xl p-5 border border-white/5 hover:border-white/10 transition-colors">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-bold text-lg text-white">{f.name}</h4>
                              <p className="text-white/40 text-xs capitalize">{f.event_type || "Event"}</p>
                            </div>
                            <div className="flex text-yellow-400">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <Icons.Star key={i} filled={i < (f.rating || 5)} />
                              ))}
                            </div>
                          </div>
                          {f.message && <p className="text-white/80 text-sm mt-3 bg-white/5 p-3 rounded-lg border border-white/5">{f.message}</p>}
                        </div>

                        <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-center gap-3 pt-3 md:pt-0 border-t md:border-t-0 border-white/5">
                          <div className="mb-1">
                            {f.published ? (
                              <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-500/10 text-green-400 border border-green-500/20 flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                                ✓ Live on Website
                              </span>
                            ) : (
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/5 text-white/40 border border-white/10">
                                Not Published
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => togglePublishFeedback(f.id)}
                            className={`w-full text-xs font-semibold px-4 py-2 rounded-lg border transition-all duration-200 ${
                              f.published
                                ? "bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20"
                                : "bg-green-500/10 text-green-400 border-green-500/30 hover:bg-green-500/20"
                            }`}
                          >
                            {f.published ? "Unpublish" : "Publish to Website"}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "creators" && (
              <div>
                <h1 className="text-2xl font-black mb-6">Creator Applications</h1>
                <div className="space-y-3">
                  {creators.map((c) => (
                    <div key={c.id} className="glass-card rounded-xl p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-bold">{c.name}</h4>
                          <p className="text-white/60 text-sm">{c.city} • {c.experience_years} years exp</p>
                          <p className="text-white/60 text-sm">{c.email}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-3 py-1 rounded-full text-sm ${c.status === "pending" ? "bg-yellow-500/20 text-yellow-400" : "bg-green-500/20 text-green-400"}`}>{c.status}</span>
                          {c.status === "pending" && (
                            <button onClick={() => approveCreator(c.id)} className="btn-primary text-sm mt-2 block">Approve</button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

// Booking Tracker Page
const BookingTrackerPage = () => {
  const [bookingId, setBookingId] = useState("");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!bookingId.trim()) return;
    setLoading(true);
    setError("");
    setBooking(null);
    try {
      const res = await axios.get(`${API}/bookings/${bookingId.trim()}`);
      setBooking(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Booking not found. Please check the ID.");
    }
    setLoading(false);
  };

  const steps = [
    { id: "inquiry", label: "Inquiry" },
    { id: "confirmed", label: "Confirmed" },
    { id: "assigned", label: "Creator Assigned" },
    { id: "in_progress", label: "In Progress" },
    { id: "completed", label: "Completed" }
  ];

  const getStepIndex = (status) => steps.findIndex(s => s.id === status);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-16 flex flex-col">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-black mb-2">Track Your <span className="text-red-500">Booking</span></h1>
          <p className="text-white/60">Enter your Booking ID to see live status</p>
        </div>
        
        <form onSubmit={handleTrack} className="flex gap-2 max-w-md mx-auto w-full mb-12">
          <input 
            type="text" 
            placeholder="e.g. CCG-20260520-ABCD" 
            value={bookingId} 
            onChange={(e) => setBookingId(e.target.value)}
            className="input-field flex-1"
            required
          />
          <button type="submit" disabled={loading} className="btn-primary whitespace-nowrap">
            {loading ? "Tracking..." : "Track"}
          </button>
        </form>

        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl text-center max-w-md mx-auto w-full">
            {error}
          </div>
        )}

        {booking && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card rounded-2xl p-6 md:p-10">
            <div className="flex justify-between items-start border-b border-white/10 pb-6 mb-8">
              <div>
                <p className="text-white/50 text-sm mb-1">Booking ID</p>
                <h3 className="text-xl font-bold font-mono">{booking.booking_id}</h3>
              </div>
              <div className="text-right">
                <p className="text-white/50 text-sm mb-1">Event Date</p>
                <p className="font-semibold">{new Date(booking.event_date).toLocaleDateString()}</p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-white/10 md:left-0 md:right-0 md:top-[15px] md:bottom-auto md:h-0.5 md:w-full" />
              <div className="flex flex-col md:flex-row justify-between gap-6 md:gap-2 relative z-10">
                {steps.map((step, i) => {
                  const currentIndex = getStepIndex(booking.status);
                  const isCompleted = i <= currentIndex;
                  const isCurrent = i === currentIndex;
                  
                  return (
                    <div key={step.id} className="flex md:flex-col items-center gap-4 md:gap-3 text-left md:text-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${isCompleted ? 'bg-red-500 border-red-500 text-white' : 'bg-black border-white/20 text-white/30'} ${isCurrent ? 'shadow-[0_0_15px_rgba(239,68,68,0.5)]' : ''}`}>
                        {isCompleted ? <Icons.Check /> : <span>{i + 1}</span>}
                      </div>
                      <div>
                        <p className={`font-bold text-sm md:text-base ${isCompleted ? 'text-white' : 'text-white/40'}`}>{step.label}</p>
                        {isCurrent && <p className="text-red-400 text-xs font-semibold mt-1 hidden md:block">Current Stage</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-10 p-4 bg-white/5 rounded-xl border border-white/10">
              <h4 className="font-bold mb-2">Package Details</h4>
              <div className="flex justify-between text-sm text-white/70">
                <span>{booking.package_type.replace('_', ' ').toUpperCase()}</span>
                <span>Total: ₹{booking.total_price?.toLocaleString()}</span>
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

// Main App
function App() {
  return (
    <AuthProvider>
      <Toaster theme="dark" position="top-right" />
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/book" element={<BookingPage />} />
            <Route path="/track" element={<BookingTrackerPage />} />
            <Route path="/booking-success" element={<BookingSuccessPage />} />
            <Route path="/join-creator" element={<JoinCreatorPage />} />
            <Route path="/waitlist" element={<WaitlistPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/contract/:id" element={<ContractPage />} />
            <Route path="/onboarding/:id" element={<OnboardingPage />} />
            <Route path="/refund-policy" element={<LegalPage type="refund-policy" />} />
            <Route path="/terms" element={<LegalPage type="terms" />} />
            <Route path="/privacy" element={<LegalPage type="privacy-policy" />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
